import React, { FC, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useFormContext, Controller, ControllerRenderProps } from 'react-hook-form';
import { Dropdown } from 'react-bootstrap';
import { useUncontrolledInput } from 'modules/core/hooks';
import { IFormField } from 'modules/core/types/form';
import { useTranslation } from 'react-i18next';
import { PREFERENCES_PE_FIELD_NAMES } from 'modules/core/shared';
import { CreditScoreColors } from 'modules/ProbabilityEstimator/enums/creditScoreColors.enum';
import { getDealSourceHighLtvLimit, getIsTierLockedByRedirect, getTiers } from 'store/selectors/lenders.selector';

import {
  DefaultToggle,
  OptionItem,
  ScoreMenu,
  SelectedScoreToggle,
  CheckmarkIcon,
  OptionContent,
  TextWrapper,
  CheckmarkSpacer,
} from '../../ProbabilityEstimator/CreditScoreDropdown/styles';
import { ITier } from '../types/Lender.types';

export const LenderTierDropdown: FC<IFormField> = ({ name, validate, disabled, onToggle }) => {
  const { t } = useTranslation();
  const { watch } = useFormContext();
  const { controlField, setValue } = useUncontrolledInput(name);
  const interestRateList = useSelector(getTiers);
  const dealSourceHighLtvLimit = useSelector(getDealSourceHighLtvLimit);
  const isTierLockedByRedirect = useSelector(getIsTierLockedByRedirect);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const selectedScore = watch(name) as ITier;

  const isSelectedDefault = selectedScore.color === CreditScoreColors.DEFAULT;
  const toggleTitle = isSelectedDefault ? t('components.creditScore.title') : selectedScore.tier_name;
  const ToggleComponent = isSelectedDefault ? DefaultToggle : SelectedScoreToggle;

  const renderOptions = useCallback(
    ({ onChange, onBlur }: ControllerRenderProps) =>
      interestRateList.map((tier) => {
        const isSelectedOption = selectedScore.tier_name === tier.tier_name;

        return (
          <OptionItem
            onClick={() => {
              onChange(tier);

              // Max LTV received from the deal source (R1) must not be overwritten by the selected tier
              if (!dealSourceHighLtvLimit) {
                setValue(PREFERENCES_PE_FIELD_NAMES.OVERRIDE_LTV, tier.ltv_limit);
              }
            }}
            onBlur={onBlur}
            color={tier.color}
            selected={isSelectedOption}
            key={tier.tier_name}
            data-testid="tier-option"
          >
            <OptionContent>
              <TextWrapper>
                {isSelectedOption ? <CheckmarkIcon color={tier.color as any} /> : <CheckmarkSpacer />}
                {tier.tier_name}
              </TextWrapper>
            </OptionContent>
          </OptionItem>
        );
      }),
    [interestRateList, selectedScore, setValue, dealSourceHighLtvLimit],
  );

  const renderDropdown = useCallback(
    (renderProps) => (
      <Dropdown
        data-testid="tierDropdown"
        onToggle={(isOpen: boolean) => {
          setIsOpen(isOpen);
          onToggle && onToggle(isOpen);
        }}
      >
        <ToggleComponent
          disabled={disabled || isTierLockedByRedirect}
          color={selectedScore.color}
          $isOpen={isOpen}
          data-testid="default-toggle"
        >
          {toggleTitle}
        </ToggleComponent>
        <ScoreMenu>{renderOptions(renderProps)}</ScoreMenu>
      </Dropdown>
    ),
    [
      ToggleComponent,
      disabled,
      isTierLockedByRedirect,
      selectedScore.color,
      isOpen,
      toggleTitle,
      renderOptions,
      onToggle,
    ],
  );

  return (
    <Controller
      name={name}
      render={renderDropdown}
      control={controlField}
      rules={{
        validate,
      }}
    />
  );
};
